import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from '../../../components/common/StyledComponent/CommonStyledComponent';
import { Icon } from '@vds/icons';
import { useSelector } from 'react-redux';
import { decodeHTML } from '../../../utility/common';
import {contentClick} from "../../../common/Analytics/GAWrapper.jsx";


const FooterShell = styled.footer`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  padding: 32px 0 64px;
  overflow: hidden;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(170, 168, 163, 1) 100%
  );

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 48px;
  }
`;

const FooterRow = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1272px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0;
    gap: 12px;
  }
`;

const QueryInputWrapper = styled.div`
  width: 591px;
  min-height: 64px;
  height: 64px;
  border-radius: 9999px;
  border: 2px solid rgba(170, 168, 163, 0.4);
  background: #ffffff;
  display: flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  padding: 12px 32px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.06);
  transition: border-color 0.15s ease;

  &:focus-within {
    border: 2px solid #000;

    svg path {
      fill: #000;
    }
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex: 1;
    width: auto;
    min-width: 0;
    min-height: 48px;
    height: 48px;
    padding: 0 24px;
    gap: 8px;
    border: 1px solid rgba(170, 168, 163, 0.4);
    
    &:focus-within {
      border: 1px solid #000;
    }
  }
`;

const QueryInput = styled.input`
  flex: 1;
  min-width: 0;
  width: 0;
  border: none;
  background: transparent;
  outline: none;
  box-sizing: border-box;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 0;
  }

  &::placeholder {
    color: ${props => props.disabled ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 1)'};
  }
`;

const QueryClearButton = styled.button`
  border: none;
  margin-left: 12px;
  background: transparent;
  color: rgba(0, 0, 0, 1);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  padding: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-left: 8px;
  }
`;

const QueryText = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  .footer-search-icon {
    width: 24px;
    height: 24px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    .footer-search-icon {
      width: 20px;
      height: 20px;
    }
  }
`;

const SearchButton = styled.button`
  height: 64px;
  width: 113px;
  padding: 12px 24px;
  border-radius: 9999px;
  box-sizing: border-box;
  border: none;
  outline: none;
  background: ${props => props.$active ? '#333333' : '#b3b3b3'};
  opacity: 1;
  color: #ffffff;
  cursor: ${props => props.$active ? 'pointer' : 'not-allowed'};
  transition: background 0.15s ease;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.06);

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 1px solid #333333;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const CircleButton = styled.button`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    border-radius: 50%;
    border: none;
    background: ${props => props.$active ? '#333333' : '#c9c9c9'};
    cursor: ${props => props.$active ? 'pointer' : 'not-allowed'};
    transition: background 0.15s ease;

    &:focus {
      outline: none;
    }

    &:focus-visible {
       outline: 1px solid #333333;
    }
  }
`;

const AskAIFooter = ({ disabled = false, onSubmitFollowUp, isLoading, onInputChange, resultsCount = 0 }) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);
  const isActive = inputValue.trim().length > 0 && !isLoading;
  const showClear = inputValue.length > 0;
  const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
  const askAiData = useSelector((state) => state.Aem?.aemAiSearchData);

  const handleClear = () => {
    setInputValue('');
    onInputChange?.('');
    inputRef.current?.focus();
  };

  const handleSubmit = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;
    if (typeof onSubmitFollowUp === 'function') {
       contentClick(
        trimmed,
        window.location.href,
        '', 
        'follow_up_search_button',
      );
      onSubmitFollowUp(trimmed);
    }
    setInputValue('');
    onInputChange?.(trimmed);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <FooterShell>
      <FooterRow>
        <QueryInputWrapper>
          <QueryText aria-hidden="true">
            {/* <img
              src={askAiData?.resultsPageTexts?.aiSchBarIImg}
              alt="Search Icon"
              width="24"
              height="24"
              
            /> */}
            <svg width={isMobile ? '20' : "24"} height={isMobile ? '20' : "24"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.7426 22.1051C12.2656 22.1051 11.8372 21.7971 11.6249 21.4916L11.5762 21.4132C11.3824 21.0861 11.3009 20.7639 11.2429 20.4849C11.204 20.3171 11.146 20.0486 11.0793 19.7375C10.9213 19.0031 10.7053 17.9977 10.5473 17.4274C10.4868 17.22 10.3405 16.9521 10.135 16.9077C9.91715 16.8583 9.56473 16.783 9.16973 16.6978C8.54328 16.5633 7.81129 16.4059 7.34284 16.2973C6.86946 16.1942 6.36768 16.0665 6.03008 15.8603C5.95355 15.8196 5.89985 15.7739 5.87023 15.7486C5.56287 15.4931 5.39684 15.1376 5.39684 14.7593C5.39684 14.3809 5.56287 14.0248 5.85295 13.7835C5.89553 13.7477 5.95231 13.6995 6.02946 13.6582C6.36953 13.4496 6.8744 13.3218 7.3268 13.2249C7.80142 13.1151 8.51427 12.9614 9.13208 12.8287C9.5419 12.7404 9.90974 12.6614 10.1307 12.6114C10.3405 12.5658 10.4868 12.2979 10.5498 12.0819C10.7053 11.5202 10.9213 10.5148 11.0793 9.78038C11.146 9.46931 11.204 9.20083 11.2453 9.02123C11.3009 8.75461 11.3824 8.43305 11.5459 8.15531L11.6206 8.03311C11.8366 7.72143 12.2649 7.41283 12.7426 7.41283C13.2216 7.41283 13.6493 7.72205 13.861 8.02879L13.9085 8.10532C14.1042 8.43614 14.185 8.75707 14.2424 9.03419C14.2819 9.20515 14.3412 9.48227 14.4103 9.8026C14.5671 10.5303 14.7812 11.5264 14.9374 12.0905C14.9979 12.2973 15.1448 12.5658 15.3497 12.6102C15.5756 12.6614 15.9452 12.741 16.3569 12.8293C16.9735 12.962 17.6839 13.1151 18.1424 13.2212C18.609 13.3218 19.1139 13.449 19.4546 13.6576C19.5299 13.6983 19.5836 13.7434 19.6132 13.768C19.9224 14.0266 20.0885 14.3821 20.0885 14.7593C20.0885 15.1364 19.9224 15.4912 19.6336 15.7338C19.5885 15.7708 19.5317 15.8184 19.4558 15.8597C19.1182 16.0665 18.6165 16.1942 18.1671 16.2917C17.6746 16.4053 16.9445 16.5627 16.3193 16.6966C15.9224 16.7824 15.5681 16.8583 15.3534 16.9065C15.1448 16.9521 14.9979 17.2206 14.9349 17.4366C14.7812 17.9915 14.5671 18.9876 14.4103 19.7153C14.3412 20.0356 14.2819 20.3127 14.2393 20.4973C14.185 20.7614 14.1042 21.083 13.9381 21.3632L13.8703 21.4768C13.6487 21.7965 13.221 22.1051 12.7426 22.1051ZM18.8026 14.7369C18.7141 14.68 18.4866 14.5852 17.8925 14.4588C17.4059 14.345 16.698 14.1933 16.0913 14.067C15.6742 13.9785 15.3013 13.8963 15.0738 13.8457C14.4481 13.7067 13.9425 13.1821 13.7213 12.4364C13.5506 11.8296 13.3421 10.85 13.1778 10.0664C13.1082 9.75669 13.0514 9.48493 13.0071 9.30163C12.9755 9.1373 12.925 8.92244 12.8491 8.79603C12.8491 8.79603 12.8372 8.76757 12.8196 8.74337C12.8087 8.72835 12.768 8.67116 12.7426 8.66483C12.7243 8.67265 12.6825 8.71494 12.6636 8.74021C12.6525 8.75426 12.6361 8.7801 12.6153 8.82763C12.5647 8.91612 12.5141 9.12469 12.4826 9.28898C12.4383 9.47861 12.3814 9.74404 12.3119 10.0474C12.1539 10.7931 11.9327 11.8233 11.7684 12.4301C11.5472 13.1821 11.0353 13.7067 10.4033 13.8457C10.1821 13.8963 9.81552 13.9721 9.39837 14.067C8.78535 14.1933 8.08383 14.345 7.62877 14.4525C6.99677 14.5852 6.76926 14.68 6.68709 14.7369L6.6555 14.7621L6.67081 14.773L6.69131 14.7839C6.77347 14.8408 6.99677 14.9265 7.58453 15.0592C8.09012 15.173 8.81694 15.331 9.43629 15.4637C9.83446 15.5458 10.1947 15.6217 10.4096 15.6722C11.0353 15.8113 11.5472 16.3358 11.7621 17.0816C11.9327 17.6946 12.1539 18.7248 12.3119 19.4706C12.3814 19.7739 12.4383 20.0394 12.4762 20.2163C12.5141 20.3933 12.5647 20.6018 12.6342 20.7219C12.6406 20.7283 12.6442 20.7453 12.6671 20.7737C12.6787 20.7887 12.7174 20.8354 12.7426 20.8417C12.7653 20.8367 12.802 20.7951 12.821 20.7698C12.8371 20.7487 12.8457 20.7308 12.8681 20.6903C12.925 20.5955 12.9755 20.3807 13.0071 20.229C13.0514 20.033 13.1082 19.7613 13.1778 19.4516C13.3421 18.6679 13.5506 17.6883 13.7213 17.0879C13.9425 16.3358 14.4481 15.8113 15.0801 15.6722C15.295 15.6217 15.6552 15.5458 16.0534 15.4637C16.6727 15.331 17.3932 15.173 17.8546 15.0655C18.4866 14.9265 18.7141 14.838 18.8026 14.7811L18.8271 14.7618L18.8216 14.7558L18.8026 14.7369ZM11.1892 5.631C11.3824 5.79209 11.4928 6.02909 11.4928 6.28091C11.4928 6.53148 11.383 6.76787 11.191 6.92896L11.096 6.99746L11.0966 6.9987C10.8824 7.13757 10.5652 7.21842 10.2825 7.2789C9.99862 7.34432 9.55979 7.43876 9.18948 7.51837L8.65129 7.63502C8.59142 7.6486 8.52168 7.7492 8.48588 7.87079C8.39577 8.19728 8.27357 8.76757 8.18099 9.19713L8.08286 9.6483C8.04891 9.81 7.9983 10.0087 7.894 10.184L7.85388 10.2507C7.67613 10.5087 7.38605 10.6673 7.10708 10.6673C6.82749 10.6673 6.53741 10.5087 6.36768 10.2624L6.3288 10.1988C6.21524 10.0069 6.16524 9.80938 6.12945 9.63904L6.03378 9.20207C5.94182 8.77189 5.819 8.19913 5.72581 7.86462C5.69186 7.7492 5.62212 7.6486 5.5641 7.63564L5.0148 7.51652C4.64634 7.43752 4.21245 7.34371 3.93595 7.27952C3.66994 7.22274 3.37739 7.14929 3.1657 7.02771L3.10687 6.99147L3.03362 6.93821C2.83056 6.76787 2.7207 6.53148 2.7207 6.28091C2.7207 6.02909 2.83118 5.79209 3.02436 5.631L3.11817 5.56373L3.11756 5.56311C3.32802 5.4261 3.64279 5.34586 3.92237 5.28476C4.20813 5.21872 4.63276 5.12738 4.99629 5.04899L5.56225 4.92679C5.62212 4.91321 5.69186 4.81261 5.72766 4.69102C5.819 4.36206 5.94182 3.78992 6.03378 3.35974L6.1313 2.91351C6.16524 2.75304 6.21524 2.55554 6.31831 2.37964L6.36707 2.30003C6.53741 2.05315 6.82749 1.89453 7.10708 1.89453C7.38605 1.89453 7.67613 2.05315 7.84585 2.29941L7.8835 2.36051C7.99892 2.55431 8.04891 2.75243 8.08471 2.92277L8.18099 3.36468C8.27357 3.79424 8.39577 4.36391 8.48773 4.69843C8.52168 4.81261 8.59142 4.91321 8.64944 4.92617L9.20799 5.04714C9.57399 5.12614 10.0029 5.2181 10.277 5.28167C10.5516 5.34154 10.8429 5.4156 11.0515 5.53596L11.0986 5.56513L11.1799 5.6236L11.1892 5.631ZM3.8409 6.28091C3.91188 6.30127 4.00631 6.32534 4.13037 6.35188C4.42353 6.41977 4.85125 6.51173 5.21415 6.5895L5.76963 6.71047C6.1745 6.79935 6.49976 7.13325 6.6374 7.60416C6.738 7.96584 6.86699 8.56513 6.9608 9.00272L7.05585 9.43598C7.06387 9.47672 7.08177 9.56189 7.10523 9.63102C7.13362 9.55572 7.14966 9.47795 7.15645 9.44524L7.25459 8.99778C7.35025 8.55093 7.47801 7.95843 7.57429 7.61095C7.71378 7.13325 8.03904 6.79935 8.44576 6.70985L8.99074 6.59135C9.35489 6.51297 9.78754 6.42039 10.0628 6.3562C10.1974 6.32719 10.298 6.30189 10.3733 6.28091C10.3029 6.26054 10.2109 6.23708 10.09 6.21116C9.79309 6.14265 9.3697 6.05193 9.00864 5.97416L8.44391 5.85134C8.03904 5.76247 7.71378 5.42857 7.57614 4.95765C7.47801 4.60276 7.35025 4.01088 7.25459 3.56403L7.1583 3.12583C7.15028 3.08509 7.13238 2.9993 7.10893 2.93079C7.08054 3.00547 7.06449 3.08386 7.0577 3.11657L6.9608 3.55909C6.86452 4.00656 6.73615 4.60215 6.63925 4.95086C6.49976 5.42857 6.1745 5.76247 5.76777 5.85196L5.19564 5.97601C4.83643 6.05316 4.41859 6.14327 4.15012 6.20499C4.01557 6.23462 3.91558 6.25992 3.8409 6.28091ZM21.0531 5.80135C21.1975 5.92232 21.2802 6.10007 21.2802 6.28831C21.2802 6.47656 21.1975 6.65431 21.0531 6.77527L20.9778 6.82959C20.8315 6.92278 20.6316 6.97463 20.4372 7.01721C20.2588 7.05857 19.9953 7.11535 19.765 7.16472L19.3836 7.24743C19.383 7.24989 19.3583 7.28261 19.3423 7.33692C19.2855 7.54183 19.209 7.90041 19.1497 8.17383L19.0843 8.47502C19.0614 8.58364 19.0269 8.71943 18.9534 8.8441L18.9183 8.9015C18.7924 9.08233 18.577 9.1996 18.369 9.1996C18.161 9.1996 17.9456 9.08233 17.8197 8.9015L17.7832 8.84163C17.7098 8.71572 17.6746 8.575 17.6518 8.46638L17.5931 8.19667C17.5339 7.92016 17.4537 7.54738 17.3938 7.33013C17.3796 7.28199 17.3549 7.24989 17.3463 7.24311L16.9815 7.16596C16.7494 7.11658 16.4822 7.05857 16.3094 7.01845C16.1224 6.97771 15.9378 6.93081 15.7959 6.85057L15.6928 6.78145C15.5398 6.65245 15.4577 6.47532 15.4577 6.28831C15.4577 6.1013 15.5398 5.92417 15.6829 5.8032L15.7607 5.74642C15.907 5.65384 16.107 5.602 16.3008 5.56003C16.4884 5.51682 16.7686 5.45634 17.0056 5.40573L17.3568 5.3292C17.3549 5.32735 17.3796 5.29525 17.395 5.24094C17.4543 5.02739 17.5345 4.65214 17.5944 4.37502L17.6536 4.10284C17.6746 4.00162 17.7098 3.8609 17.7832 3.73499L17.8197 3.67513C18.0715 3.31345 18.6664 3.31345 18.9183 3.67513L18.9534 3.73253C19.0269 3.8572 19.0614 3.99298 19.0861 4.11025L19.1491 4.39847C19.2077 4.67251 19.2855 5.03356 19.3441 5.24649C19.3583 5.29463 19.3836 5.32735 19.3923 5.33413L19.7428 5.40758C19.978 5.45819 20.2526 5.51744 20.4285 5.55756C20.6155 5.59829 20.8001 5.64582 20.942 5.72605L21.0023 5.76322L21.0531 5.80135ZM16.6618 6.28831C16.8161 6.32287 16.9902 6.35991 17.1475 6.39385L17.5296 6.47717C17.8215 6.54013 18.056 6.77898 18.1542 7.11473C18.2184 7.34803 18.3029 7.73994 18.3653 8.03002L18.369 8.04669L18.3776 8.0078C18.4393 7.71958 18.5214 7.34247 18.5825 7.12028C18.6837 6.77836 18.9183 6.54013 19.2127 6.47594L19.599 6.392C19.7564 6.35805 19.9298 6.32102 20.0767 6.28831C19.9181 6.25313 19.7379 6.21487 19.5774 6.1803L19.2096 6.10069C18.9176 6.0365 18.6831 5.79765 18.5844 5.46313C18.5208 5.23292 18.4387 4.85334 18.3764 4.5645L18.369 4.52994L18.3665 4.54166C18.3042 4.83236 18.219 5.22674 18.1554 5.45634C18.056 5.79703 17.8221 6.03588 17.5271 6.10069L17.1716 6.17783C17.0056 6.21363 16.8186 6.25375 16.6618 6.28831Z" fill="#716F6D" />
            </svg>

          </QueryText>
          <QueryInput
            ref={inputRef}
            type="text"
            aria-label={decodeHTML(askAiData?.resultsPageTexts?.schPchText)}
            placeholder={isMobile ? decodeHTML(askAiData?.resultsPageTexts?.schPchTextMob) : decodeHTML(askAiData?.resultsPageTexts?.schPchText)}
            className='font-eDS-bold vds-title-sm mobile-vds-body-lg'
            value={inputValue}
            disabled={disabled || isLoading}
            aria-disabled={disabled || isLoading}
            onChange={e => { setInputValue(e.target.value); onInputChange?.(e.target.value); }}
            onKeyDown={handleKeyDown}
            
          />
          {showClear && (
            <QueryClearButton
              type="button"
              onClick={handleClear}
              className="vds-body-lg mobile-vds-body-sm"
              aria-label="Clear input"
            >
              {askAiData?.resultsPageTexts?.clearText}
            </QueryClearButton>
          )}
        </QueryInputWrapper>
        <SearchButton type="button" aria-label={askAiData?.resultsPageTexts?.schBtnAccessTxt || 'Ask AI search'} disabled={!isActive} $active={isActive} onClick={handleSubmit}>
          <div className='vds-title-xs font-eDS-bold'>{askAiData?.resultsPageTexts?.schBtnText}</div>
        </SearchButton>
        <CircleButton type="button" aria-label={askAiData?.resultsPageTexts?.schBtnAccessTxt || 'Ask AI search'} disabled={!isActive} $active={isActive} onClick={handleSubmit}>
          <Icon name="right-arrow" size='medium' color='#fff' />
        </CircleButton>
      </FooterRow>
    </FooterShell>
  );
};

export default AskAIFooter;
